/**
 * Read an operator roster file in the browser into string rows. CSV goes through lib/csv's
 * tokenizer; .xlsx through read-excel-file (loaded on demand so the parser stays out of the
 * main bundle). The original file never leaves the browser.
 */
import { parseCsv } from './csv';
import { cellToString } from './importMap';

export interface SheetRead {
  /** Worksheet name for .xlsx, undefined for CSV. */
  sheetName?: string;
  rows: string[][];
}

export function isExcelFile(name: string): boolean {
  return /\.xlsx$/i.test(name);
}

/** Shown when the .xlsx parser fails or never answers (e.g. its Worker is blocked). */
export const XLSX_READ_FAILED = '無法解析這個 Excel 檔(瀏覽器擋下了解析程式,或檔案已損壞);可另存成 CSV 再匯入';
export const XLSX_READ_TIMEOUT_MS = 30_000;

/** Rejects with `message` when `p` has not settled within `ms` — a blocked Worker never answers. */
export function withTimeout<T>(p: Promise<T>, ms: number, message: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const t = setTimeout(() => reject(new Error(message)), ms);
    p.then(
      (v) => { clearTimeout(t); resolve(v); },
      (e) => { clearTimeout(t); reject(e); },
    );
  });
}

async function readXlsx(file: File): Promise<SheetRead> {
  const mod = await import('read-excel-file/browser');
  const sheets = await mod.default(file);
  const first = sheets[0];
  if (!first) return { rows: [] };
  return {
    sheetName: first.sheet,
    rows: first.data.map((r) => r.map((c) => cellToString(c))),
  };
}

export async function readSheetFile(file: File, timeoutMs = XLSX_READ_TIMEOUT_MS): Promise<SheetRead> {
  if (isExcelFile(file.name)) {
    try {
      return await withTimeout(readXlsx(file), timeoutMs, XLSX_READ_FAILED);
    } catch (e) {
      // The parser's own messages are English and technical (「Doesn't look like an .xlsx file」), and a
      // Worker failure has none — the operator always gets the one zh sentence; the cause stays in console.
      console.warn('xlsx read failed', e);
      throw new Error(XLSX_READ_FAILED);
    }
  }
  return { rows: parseCsv(await file.text()) };
}
